/**
 * VAT display toggle (INC VAT / EX VAT)
 * - Persists choice in localStorage.
 * - Rewrites displayed prices (theme prices + Apippa/CPC calculator prices) by the VAT rate.
 * - Appends an "inc VAT" / "ex VAT" note next to prices.
 * - Uses a MutationObserver so dynamically updated prices (variant changes, cart updates,
 *   Apippa price calculator output) are converted too.
 *
 * Display-only: cart/checkout totals are still calculated by Shopify's tax settings.
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'vat-display-mode';

  const cfg = {
    rate: 1.2,
    incNote: 'inc VAT',
    exNote: 'ex VAT',
    defaultMode: 'ex'
  };

  // Theme price elements (server-rendered).
  const PRICE_SELECTORS = [
    '.price__current .js-value',
    '.price__was .js-value',
    'cpc.original_price',
    'cpc.final_line_price',
    'cpc.original_line_price',
    'cpc.cart_total_price',
    '[data-vat-price]'
  ];

  // "Was"/compare-at prices get converted but don't need their own VAT note.
  const NO_NOTE_SELECTORS = ['.price__was .js-value', 'cpc.original_price', 'cpc.original_line_price'];

  // Containers rendered by the Apippa "Custom Price Calculator" (CPC) app. The app's
  // dynamic price banner lives inside one of these; we convert any money value found within.
  const APP_CONTAINER_SELECTOR = '[id*="cpc" i], [class*="cpc" i], [id*="apippa" i], [class*="apippa" i]';

  const MONEY_SRC = '([£$€])(\\s*)(\\d[\\d.,]*\\d|\\d)';
  const MONEY_TEST = new RegExp(MONEY_SRC);
  const BARE_NUM_TEST = /^\s*\d[\d.,]*\s*$/;

  let mode = null;

  const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /** Parse "1,220.50" / "20" / "20.5" into a number. */
  function toValue(numStr) {
    const sep = Math.max(numStr.lastIndexOf('.'), numStr.lastIndexOf(','));
    if (sep > -1 && numStr.length - sep - 1 <= 2) {
      return parseFloat(`${numStr.slice(0, sep).replace(/[.,]/g, '')}.${numStr.slice(sep + 1)}`);
    }
    return parseFloat(numStr.replace(/[.,]/g, ''));
  }

  function formatAmount(value) {
    return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** Multiply every money amount found in the string by the VAT rate. */
  function convertString(str) {
    return str.replace(new RegExp(MONEY_SRC, 'g'), (match, sym, space, num) => {
      const value = toValue(num);
      if (Number.isNaN(value)) return match;
      return sym + space + formatAmount(value * cfg.rate);
    });
  }

  /**
   * Element content as a plain money string. Superscript decimals (the theme's
   * "supercents" setting) render as "£20<sup>23</sup>", so re-insert the decimal point.
   */
  function elToString(el) {
    let out = '';
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SUP') {
        out += `.${node.textContent}`;
      } else if (!(node.nodeType === Node.ELEMENT_NODE && node.classList.contains('vat-note'))) {
        out += node.textContent;
      }
    });
    return out;
  }

  function ensureNote(el) {
    let note = el.nextElementSibling;
    if (!note || !note.classList.contains('vat-note')) {
      note = document.createElement('span');
      note.className = 'vat-note';
      el.after(note);
    }
    const text = mode === 'inc' ? cfg.incNote : cfg.exNote;
    if (note.textContent !== text) note.textContent = text;
  }

  function applyEl(el, opts) {
    const { withNote, allowBare } = opts;
    const currentHtml = el.innerHTML;

    if (mode === 'inc') {
      // Skip if this element still shows exactly what we last wrote.
      if (el.dataset.vatConverted !== currentHtml) {
        const source = elToString(el);
        let converted = null;

        if (MONEY_TEST.test(source)) {
          converted = convertString(source);
        } else if (allowBare && BARE_NUM_TEST.test(source)) {
          // Bare amount (e.g. Apippa's priceTicker label, where the "£" lives in the parent).
          const value = toValue(source.trim());
          if (!Number.isNaN(value)) converted = formatAmount(value * cfg.rate);
        }

        if (converted !== null && converted !== source) {
          let html = escapeHtml(converted);
          if (el.querySelector('sup')) {
            html = html.replace(/(\d)\.(\d{2})(?!\d)/, '$1<sup>$2</sup>');
          }
          el.dataset.vatBase = currentHtml;
          el.innerHTML = html;
          el.dataset.vatConverted = el.innerHTML;
        } else if (converted === null) {
          return;
        }
      }
    } else {
      // Restore the original (ex VAT) content if we previously converted it.
      if (el.dataset.vatConverted !== undefined && el.dataset.vatConverted === currentHtml) {
        el.innerHTML = el.dataset.vatBase || currentHtml;
      }
      delete el.dataset.vatBase;
      delete el.dataset.vatConverted;
      const source = elToString(el);
      if (!MONEY_TEST.test(source) && !(allowBare && BARE_NUM_TEST.test(source))) return;
    }

    if (withNote) ensureNote(el);
  }

  /** Do the parent's own text nodes contain a currency symbol? */
  function parentHasCurrencyText(el) {
    const parent = el.parentElement;
    if (!parent) return false;
    let text = '';
    parent.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) text += node.textContent;
    });
    return /[£$€]/.test(text);
  }

  /** Find leaf elements holding a money value inside Apippa/CPC app containers. */
  function appPriceEls() {
    const els = [];
    document.querySelectorAll(APP_CONTAINER_SELECTOR).forEach((container) => {
      if (container.closest('vat-toggle')) return;
      [container, ...container.querySelectorAll('*')].forEach((el) => {
        if (el.matches('script, style, noscript, input, select, textarea, option, .vat-note')) return;
        const hasElementChildren = [...el.children].some((c) => c.tagName !== 'SUP' && !c.classList.contains('vat-note'));
        if (hasElementChildren) return;
        const text = elToString(el);
        if (text.length > 40) return;
        if (MONEY_TEST.test(text)) {
          els.push({ el, allowBare: false });
        } else if (BARE_NUM_TEST.test(text) && (el.hasAttribute('data-origmoney') || parentHasCurrencyText(el))) {
          // Amount split from its currency symbol (e.g. "£ <label>334.15</label>").
          els.push({ el, allowBare: true });
        }
      });
    });
    return els;
  }

  function updateToggles() {
    document.querySelectorAll('vat-toggle').forEach((toggle) => {
      toggle.classList.toggle('vat-toggle--inc', mode === 'inc');
      toggle.classList.toggle('vat-toggle--ex', mode !== 'inc');
      const btn = toggle.querySelector('.vat-toggle__btn');
      if (btn) btn.setAttribute('aria-checked', mode === 'inc' ? 'true' : 'false');
    });
  }

  function applyAll() {
    const targets = new Map();

    PRICE_SELECTORS.forEach((selector) => {
      const withNote = !NO_NOTE_SELECTORS.includes(selector);
      document.querySelectorAll(selector).forEach((el) => {
        if (!targets.has(el)) targets.set(el, { withNote, allowBare: false });
      });
    });

    appPriceEls().forEach(({ el, allowBare }) => {
      if (!targets.has(el)) targets.set(el, { withNote: true, allowBare });
    });

    targets.forEach((opts, el) => applyEl(el, opts));

    document.documentElement.classList.toggle('vat-mode-inc', mode === 'inc');
    document.documentElement.classList.toggle('vat-mode-ex', mode !== 'inc');
    updateToggles();
  }

  function setMode(newMode) {
    mode = newMode === 'inc' ? 'inc' : 'ex';
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      /* private browsing */
    }
    applyAll();
    document.dispatchEvent(new CustomEvent('on:vat-mode:change', { detail: { mode } }));
  }

  if (!customElements.get('vat-toggle')) {
    customElements.define('vat-toggle', class extends HTMLElement {});
  }

  // Delegated listener: survives header re-renders and custom element timing issues.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('.vat-toggle__btn');
    if (!btn) return;
    event.preventDefault();
    setMode(mode === 'inc' ? 'ex' : 'inc');
  });

  let scheduled = false;

  function init() {
    const toggleEl = document.querySelector('vat-toggle');
    if (toggleEl) {
      const rate = parseFloat(toggleEl.dataset.vatRate);
      if (!Number.isNaN(rate) && rate >= 0) cfg.rate = 1 + rate / 100;
      if (toggleEl.dataset.incNote) cfg.incNote = toggleEl.dataset.incNote;
      if (toggleEl.dataset.exNote) cfg.exNote = toggleEl.dataset.exNote;
      if (toggleEl.dataset.defaultMode) cfg.defaultMode = toggleEl.dataset.defaultMode;
    }

    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      /* private browsing */
    }
    mode = stored === 'inc' || stored === 'ex' ? stored : cfg.defaultMode;

    applyAll();

    // Re-apply whenever prices change dynamically (variant switch, cart drawer refresh,
    // Apippa calculator recalculation). applyAll() is idempotent, so this settles.
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        applyAll();
      });
    }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
